/**
 * ChatDashboard.tsx — Chat analítico com Gemini AI
 *
 * Faz perguntas em linguagem natural sobre leads, vendas, conversão,
 * e recebe respostas baseadas em dados reais do Supabase via function calling.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const api = axios.create({ baseURL: '/api', timeout: 60000 });

// ─── Types ─────────────────────────────────────────────────────────────────
interface FnCall {
  fn: string;
  args: Record<string, unknown>;
  result?: any;
}

interface ChatMsg {
  id: string;
  role: 'user' | 'model';
  content: string;
  loading?: boolean;
  fn_calls?: FnCall[];
}

// ─── Suggestions ───────────────────────────────────────────────────────────
const SUGGESTIONS = [
  'Quanto tempo leva a primeira resposta para os leads?',
  'Onde os leads param de responder no script?',
  'De quais estados estão vindo os leads?',
  'Qual o horário de pico de chegada de leads?',
  'Analise os leads que não compraram',
  'Quais as principais objeções dos leads?',
  'Ticket médio por produto',
  'Volume diário de leads em março',
];

// ─── Helpers ───────────────────────────────────────────────────────────────
function uid() {
  return Math.random().toString(36).slice(2, 10);
}

/** Renderiza inline: **bold** dentro de uma string */
function renderInline(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>
      : <span key={i}>{part}</span>
  );
}

/**
 * Renderiza tabelas Markdown
 */
function renderMarkdownTable(lines: string[], startIdx: number): { element: JSX.Element; endIdx: number } {
  const tableLines: string[] = [];
  let idx = startIdx;
  
  // Coleta todas as linhas da tabela
  while (idx < lines.length && lines[idx].trim().startsWith('|')) {
    tableLines.push(lines[idx].trim());
    idx++;
  }
  
  if (tableLines.length < 2) return { element: <></>, endIdx: startIdx };
  
  // Parse header
  const headerCells = tableLines[0].split('|').filter(c => c.trim()).map(c => c.trim());
  
  // Parse body (skip separator line)
  const bodyRows = tableLines.slice(2).map(line => 
    line.split('|').filter(c => c.trim()).map(c => c.trim())
  );
  
  return {
    element: (
      <div className="overflow-x-auto my-3">
        <table className="min-w-full text-sm border-collapse">
          <thead>
            <tr className="bg-indigo-100 dark:bg-indigo-900/40">
              {headerCells.map((cell, i) => (
                <th key={i} className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200 border-b-2 border-indigo-300 dark:border-indigo-700">
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bodyRows.map((row, i) => (
              <tr key={i} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                {row.map((cell, j) => (
                  <td key={j} className="px-3 py-2 text-gray-700 dark:text-gray-300">{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
    endIdx: idx
  };
}

/**
 * Renderiza uma mensagem da MelBot em blocos visuais:
 * - Tabelas Markdown → renderizadas como HTML table estilizado
 * - Linha iniciando com "Dados" ou "Dado" → bloco de dados (cinza)
 * - Linha com "Insight" → bloco de insight (azul)
 * - Linha com "▶ Próximo Passo" → bloco de ação (verde)
 * - Linha iniciando com "•" ou "-" → item de lista
 * - Linha terminando em ":" → cabeçalho de seção
 * - Linha vazia → espaçamento
 */
function renderText(text: string) {
  const lines = text.split('\n');
  const elements: JSX.Element[] = [];
  let i = 0;
  
  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trimStart();
    
    // ── Tabela Markdown ──────────────────────────────────────────────
    if (line.startsWith('|')) {
      const { element, endIdx } = renderMarkdownTable(lines, i);
      if (endIdx > i) {
        elements.push(<div key={i}>{element}</div>);
        i = endIdx;
        continue;
      }
    }



    // ── Bloco Próximo Passo ─────────────────────────────────────────
    if (/^▶\s*Próximo Passo/i.test(line)) {
      const content = line.replace(/^▶\s*Próximo Passo\s*:?\s*/i, '');
      elements.push(
        <div key={i} className="mt-3 flex gap-2 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 px-3 py-2">
          <span className="text-emerald-500 mt-0.5 flex-shrink-0">▶</span>
          <div>
            <span className="text-xs font-bold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 block mb-0.5">Próximo Passo</span>
            <span className="text-sm text-emerald-900 dark:text-emerald-100">{renderInline(content)}</span>
          </div>
        </div>
      );
      i++;
      continue;
    }

    // ── Bloco Insight ───────────────────────────────────────────────
    if (/^\*{0,2}Insight/i.test(line)) {
      const content = line.replace(/^\*{0,2}Insight(\s+Principal)?\*{0,2}\s*:\*{0,2}\s*/i, '').replace(/^\*{1,2}\s*/, '');
      elements.push(
        <div key={i} className="mt-3 flex gap-2 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 px-3 py-2">
          <span className="text-indigo-400 flex-shrink-0 mt-0.5">💡</span>
          <div>
            <span className="text-xs font-bold uppercase tracking-wide text-indigo-500 dark:text-indigo-400 block mb-0.5">Insight</span>
            <span className="text-sm text-indigo-900 dark:text-indigo-100">{renderInline(content)}</span>
          </div>
        </div>
      );
      i++;
      continue;
    }

    // ── Bloco Dados ─────────────────────────────────────────────────
    if (/^Dado[s]?\s*(→|:|-)/i.test(line)) {
      const content = line.replace(/^Dado[s]?\s*(→|:|-)\s*/i, '');
      elements.push(
        <div key={i} className="flex gap-2 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-600 px-3 py-2">
          <span className="text-gray-400 flex-shrink-0 mt-0.5">📊</span>
          <span className="text-sm text-gray-700 dark:text-gray-200">{renderInline(content)}</span>
        </div>
      );
      i++;
      continue;
    }

    // ── Item de lista ────────────────────────────────────────────────
    if (/^[•·\-]\s+/.test(line)) {
      const content = line.replace(/^[•·\-]\s+/, '');
      elements.push(
        <div key={i} className="flex gap-2 pl-1">
          <span className="text-indigo-400 flex-shrink-0 mt-1 text-xs">●</span>
          <span className="text-sm text-gray-700 dark:text-gray-200">{renderInline(content)}</span>
        </div>
      );
      i++;
      continue;
    }

    // ── Cabeçalho de secção (linha curta terminando em :) ───────────
    if (/^[A-ZÁÉÍÓÚÂÊÎÔÛÃÕ][^:]{0,50}:$/.test(line.replace(/\*\*/g, ''))) {
      elements.push(
        <p key={i} className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mt-3 mb-0.5">
          {line.replace(/\*\*/g, '')}
        </p>
      );
      i++;
      continue;
    }

    // ── Linha vazia ──────────────────────────────────────────────────
    if (line === '') {
      elements.push(<div key={i} className="h-1" />);
      i++;
      continue;
    }

    // ── Parágrafo normal ─────────────────────────────────────────────
    elements.push(
      <p key={i} className="text-sm text-gray-800 dark:text-gray-100">
        {renderInline(line)}
      </p>
    );
    i++;
  }
  
  return elements;
}

// ─── FN label map ──────────────────────────────────────────────────────────
const FN_LABELS: Record<string, string> = {
  get_leads_count:           'Contagem de leads',
  get_leads_by_month:        'Leads por mês',
  get_sales_summary:         'Resumo de vendas',
  get_conversion_rate:       'Taxa de conversão',
  compare_months:            'Comparativo de meses',
  get_recent_leads:          'Leads recentes',
  get_top_products:          'Top produtos',
  get_leads_by_state:        'Leads por estado',
  get_leads_by_hour:         'Leads por horário (data_cadastro)',
  get_leads_by_weekday:      'Leads por dia da semana',
  get_leads_by_objective:    'Leads por objetivo',
  get_funnel_metrics:        'Métricas do funil',
  get_revenue_trend:         'Tendência de receita',
  search_lead:               'Busca de lead',
  get_sales_in_range:        'Vendas no período',
  get_response_time_metrics: 'TMR/TMA (mensagens)',
  get_funnel_dropout:        'Abandono por etapa do chatbot',
  get_leads_by_source:       'Leads por dispositivo/canal',
  get_lost_leads_analysis:   'Análise de leads perdidos',
  get_reengagement_stats:    'Stats de reengajamento',
  get_objections_patterns:   'Padrões de objeções',
  get_ticket_by_product:     'Ticket médio por produto',
  get_daily_lead_volume:     'Volume diário de leads',
};

/** Pill showing function calls with toggle */
function FnCallPills({ calls }: { calls: FnCall[] }) {
  const [open, setOpen] = useState(false);
  if (!calls || calls.length === 0) return null;
  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-300 transition-colors"
      >
        <span>🔍 {calls.length} função{calls.length > 1 ? 'ões' : ''} consultada{calls.length > 1 ? 's' : ''}</span>
        <svg
          className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="mt-1 flex flex-wrap gap-1">
          {calls.map((c, i) => {
            const label = FN_LABELS[c.fn] || c.fn;
            const argStr = Object.entries(c.args || {})
              .filter(([, v]) => v !== null && v !== undefined)
              .map(([k, v]) => `${k}: ${v}`)
              .join(', ');
            return (
              <span
                key={i}
                title={argStr || 'sem filtros'}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 text-xs border border-indigo-200 dark:border-indigo-700 cursor-default"
              >
                <span className="opacity-60">⚙</span>
                {label}
                {argStr && <span className="opacity-50 ml-0.5">({argStr})</span>}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── TypingDots ─────────────────────────────────────────────────────────────
function TypingDots() {
  return (
    <div className="flex gap-1 items-center h-5 px-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

function CorrelationChartViewer({ result }: { result: any }) {
  if (result.type !== 'CorrelationChart') return null;

  return (
    <div className="mt-4 bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
      <h3 className="text-md font-bold text-gray-800 dark:text-gray-100 mb-4">
        Correlação: Marketing vs Vendas
      </h3>
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={result.data_points}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ccc" />
            <XAxis dataKey="date" tick={{fontSize: 12}} />
            <YAxis yAxisId="left" tick={{fontSize: 12}} />
            <YAxis yAxisId="right" orientation="right" tick={{fontSize: 12}} />
            <Tooltip />
            <Legend />
            <Line yAxisId="left" type="monotone" dataKey="total_views" stroke="#8884d8" name="Visualizações" strokeWidth={2} />
            <Line yAxisId="right" type="monotone" dataKey="total_sales" stroke="#82ca9d" name="Vendas (Lag 24h)" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg border border-indigo-100 dark:border-indigo-800">
        <p className="text-sm font-semibold text-indigo-800 dark:text-indigo-300 mb-1">Análise Llama 3 (Groq):</p>
        <p className="text-sm text-gray-700 dark:text-gray-300">{result.ai_insight}</p>
        <p className="text-xs text-gray-500 mt-2 font-mono">Índice Pearson: {result.correlation_score}</p>
      </div>
    </div>
  );
}

export default function ChatDashboard() {
  const [messages, setMessages] = useState<ChatMsg[]>([
    {
      id: 'welcome',
      role: 'model',
      content:
        'Olá! Sou a **MelBot**, sua analista sênior de vendas 📊\n\n**Eficiência Operacional**\n• Tempo médio de resposta (TMR/TMA)\n• Volume diário e capacidade operacional\n\n**Funil e Conversão**\n• Onde os leads abandonam o script\n• Leads perdidos (frios, mornos, quentes) com ações\n\n**Comportamento**\n• Estado (DDD), horário de pico, dia da semana, objetivo\n\n**Inteligência de Script**\n• Objeções e perguntas mais frequentes\n• Taxa de reengajamento\n\n**Financeiro**\n• Ticket médio por produto, tendência histórica, CAC estimado\n\nPara cada dado, entrego o **insight** e o **próximo passo** concreto.',
    },
  ]);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(false);
  const bottomRef               = useRef<HTMLDivElement>(null);
  const textareaRef             = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 160) + 'px';
    }
  }, [input]);

  const sendMessage = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    // Build history (exclude loading/welcome stubs, max 20)
    const historyForApi = messages
      .filter((m) => !m.loading && m.id !== 'welcome')
      .slice(-20)
      .map((m) => ({ role: m.role, content: m.content }));

    const userMsg: ChatMsg = { id: uid(), role: 'user', content: trimmed };
    const loadingMsg: ChatMsg = { id: 'loading', role: 'model', content: '', loading: true };

    setMessages((prev) => [...prev, userMsg, loadingMsg]);
    setInput('');
    setLoading(true);

    try {
      const { data } = await api.post('/chat', {
        message: trimmed,
        history: historyForApi,
      });

      const botMsg: ChatMsg = {
        id: uid(),
        role: 'model',
        content: data.answer || 'Sem resposta.',
        fn_calls: data.fn_calls || [],
      };

      setMessages((prev) => [...prev.filter((m) => m.id !== 'loading'), botMsg]);
    } catch (err: any) {
      const errMsg: ChatMsg = {
        id: uid(),
        role: 'model',
        content: `❌ Erro: ${err?.response?.data?.detail || err.message || 'Falha na requisição'}`,
      };
      setMessages((prev) => [...prev.filter((m) => m.id !== 'loading'), errMsg]);
    } finally {
      setLoading(false);
      textareaRef.current?.focus();
    }
  }, [messages, loading]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleClear = () => {
    setMessages([{
      id: 'welcome',
      role: 'model',
      content: 'Conversa reiniciada! Como posso ajudar? 📊',
    }]);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] max-h-[860px]">
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            🤖 MelBot
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Pergunte em linguagem natural · dados em tempo real via Gemini AI
          </p>
        </div>
        <button
          onClick={handleClear}
          className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors px-2 py-1 rounded border border-gray-200 dark:border-gray-700"
        >
          🗑 Limpar
        </button>
      </div>

      {/* ── Messages area ── */}
      <div className="flex-1 overflow-y-auto space-y-4 pr-1">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {msg.role === 'model' && (
              <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-sm mr-2 flex-shrink-0 mt-0.5">
                M
              </div>
            )}
            <div
              className={`max-w-[82%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-indigo-600 text-white rounded-br-sm'
                  : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 shadow rounded-bl-sm'
              }`}
            >
              {msg.loading ? (
                <TypingDots />
              ) : (
                <>
                  <div className={`flex flex-col gap-1 ${msg.role === 'user' ? '' : ''}`}>
                    {renderText(msg.content)}
                  </div>
                  {msg.role === 'model' && msg.fn_calls && msg.fn_calls.length > 0 && (
                    <>
                      {msg.fn_calls.map((c, idx) => 
                        c.fn === '_fn_get_content_correlation' && c.result ? 
                          <CorrelationChartViewer key={idx} result={c.result} /> : null
                      )}
                      <FnCallPills calls={msg.fn_calls} />
                    </>
                  )}
                </>
              )}
            </div>
            {msg.role === 'user' && (
              <div className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-200 text-sm ml-2 flex-shrink-0 mt-0.5">
                V
              </div>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* ── Suggestion chips (only when no history yet) ── */}
      {messages.length <= 1 && (
        <div className="flex flex-wrap gap-2 my-3">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              onClick={() => sendMessage(s)}
              className="text-xs px-3 py-1.5 rounded-full border border-indigo-300 dark:border-indigo-700 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* ── Input bar ── */}
      <div className="mt-3 flex gap-2 items-end bg-white dark:bg-gray-800 rounded-2xl shadow px-3 py-2 border border-gray-200 dark:border-gray-700">
        <textarea
          ref={textareaRef}
          rows={1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          placeholder="Pergunte algo… (Enter para enviar, Shift+Enter para nova linha)"
          className="flex-1 resize-none bg-transparent text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none disabled:opacity-50"
          style={{ minHeight: '36px', maxHeight: '160px' }}
        />
        <button
          onClick={() => sendMessage(input)}
          disabled={loading || !input.trim()}
          className="flex-shrink-0 w-9 h-9 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white rounded-xl flex items-center justify-center transition-colors"
        >
          {loading ? (
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
          ) : (
            <svg className="w-4 h-4 rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
            </svg>
          )}
        </button>
      </div>
      <p className="text-xs text-center text-gray-400 dark:text-gray-600 mt-2">
        Powered by Gemini 2.0 Flash · dados direto do Supabase · nunca inventa números
      </p>
    </div>
  );
}
